/**
 * Greys out the catalog map links (desktop dropdown and phone menu) whose collection or product has
 * nothing in stock. The list comes from the section olin-map-stock through the Section Rendering API
 * when the browser is idle or on the first interaction, whichever comes first, and is kept in
 * sessionStorage for 10 minutes per language, so the check never slows down the page itself.
 * It is applied again whenever a menu panel opens, in case the theme re-rendered the menu.
 * Links keep working.
 */
const SECTION = 'olin-map-stock';
const TTL = 10 * 60 * 1000;
const root = window.Shopify?.routes?.root || '/';
const storageKey = `olin-map-stock:${root}`;

/** @returns {string[] | null} */
function readCache() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (cached && Date.now() - cached.time < TTL && Array.isArray(cached.oos)) return cached.oos;
  } catch {
    // Storage blocked or corrupt: fetch again
  }
  return null;
}

/** @returns {Promise<string[]>} */
async function fetchOutOfStock() {
  const response = await fetch(`${root}?sections=${SECTION}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) return [];
  const sections = await response.json();
  const html = sections[SECTION];
  if (typeof html !== 'string') return [];
  const script = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-olin-map-stock]');
  const oos = JSON.parse(script?.textContent || '{"oos": []}').oos || [];
  try {
    sessionStorage.setItem(storageKey, JSON.stringify({ time: Date.now(), oos }));
  } catch {
    // Storage blocked: just don't cache
  }
  return oos;
}

/** @param {string[]} oos */
function apply(oos) {
  const out = new Set(oos);
  for (const link of document.querySelectorAll('a[data-olin-stock]')) {
    if (!out.has(link.getAttribute('href'))) continue;
    link.classList.add('is-oos');
    const label = link.closest('[data-oos-label]')?.getAttribute('data-oos-label');
    if (label) link.title = label;
  }
}

/** @type {Promise<string[]> | null} */
let loading = null;

function refresh() {
  if (!document.querySelector('a[data-olin-stock]')) return;
  loading ??= (async () => readCache() ?? (await fetchOutOfStock()))().catch(() => []);
  loading.then(apply);
}

if ('requestIdleCallback' in window) {
  requestIdleCallback(refresh, { timeout: 3000 });
} else {
  setTimeout(refresh, 1500);
}
document.addEventListener('pointerover', refresh, { once: true, passive: true });
document.addEventListener('focusin', refresh, { once: true });
document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement && event.target.open) refresh();
  },
  true
);
